// =============================================================
// ReviewsPage  Phase 7+ Step 2: Reviews Product Module
// Customer-facing reputation management powered by Google Business Profile.
// Real metrics only, honest Google API status, zero fabricated reviews.
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  reviewsApi,
  Business,
  ReviewsResponseDto,
  ReviewItemDto,
} from '../lib/api';

export function ReviewsPage() {
  const { businessId } = useParams<{ businessId?: string }>();
  const navigate = useNavigate();

  // Active Business State
  const [business, setBusiness] = useState<Business | null>(null);
  const [activeBizId, setActiveBizId] = useState<number | null>(
    businessId ? parseInt(businessId, 10) : null
  );

  // Data state
  const [data, setData] = useState<ReviewsResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Filters & Pagination
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [replyStatusFilter, setReplyStatusFilter] = useState<'all' | 'unanswered' | 'replied'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reply Modal State
  const [replyingReview, setReplyingReview] = useState<ReviewItemDto | null>(null);
  const [replyComment, setReplyComment] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySuccess, setReplySuccess] = useState<string | null>(null);

  // AI Suggestion State
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Resolve businessId if missing from URL
  useEffect(() => {
    if (!businessId) {
      const stored = localStorage.getItem('active_dashboard_business_id');
      if (stored) {
        navigate(`/businesses/${stored}/reviews`, { replace: true });
        setActiveBizId(parseInt(stored, 10));
      } else {
        businessApi
          .list()
          .then((bizList) => {
            if (bizList.length > 0) {
              navigate(`/businesses/${bizList[0].id}/reviews`, { replace: true });
              setActiveBizId(bizList[0].id);
            } else {
              setLoading(false);
            }
          })
          .catch((err) => {
            console.error('Failed to load businesses:', err);
            setError('Unable to load business profile. Please try again.');
            setLoading(false);
          });
      }
    } else {
      setActiveBizId(parseInt(businessId, 10));
    }
  }, [businessId, navigate]);

  // Load business metadata
  useEffect(() => {
    if (!activeBizId) return;
    businessApi
      .get(activeBizId)
      .then((b) => setBusiness(b))
      .catch((err) => console.warn('Could not fetch business info:', err));
  }, [activeBizId]);

  // Fetch reviews
  const loadReviews = useCallback(async () => {
    if (!activeBizId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const res = await reviewsApi.getReviews(activeBizId, {
        rating: ratingFilter,
        replyStatus: replyStatusFilter,
        search: searchTerm || undefined,
        page,
        pageSize,
      });

      setData(res);
    } catch (err: any) {
      console.error('Error fetching reviews:', err);
      setError(err.message || 'Unable to load review data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeBizId, ratingFilter, replyStatusFilter, searchTerm, page, pageSize]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  // Handle Sync
  const handleSync = async () => {
    if (!activeBizId || syncing) return;
    setSyncing(true);
    try {
      await reviewsApi.sync(activeBizId);
      await loadReviews();
    } catch (err: any) {
      console.error('Sync failed:', err);
      setError(err.message || 'Synchronization failed. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Open Reply Modal
  const openReplyModal = (review: ReviewItemDto) => {
    setReplyingReview(review);
    setReplyComment(review.reviewReply?.comment || '');
    setReplyError(null);
    setReplySuccess(null);
    setAiError(null);
  };

  // Close Reply Modal
  const closeReplyModal = () => {
    setReplyingReview(null);
    setReplyComment('');
    setReplyError(null);
    setReplySuccess(null);
    setAiError(null);
  };

  // Handle Submit Reply
  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeBizId || !replyingReview || !replyComment.trim()) return;

    setReplySubmitting(true);
    setReplyError(null);
    setReplySuccess(null);

    try {
      await reviewsApi.replyToReview(
        activeBizId,
        replyingReview.reviewId,
        replyComment.trim()
      );

      setReplySuccess('Reply successfully published to Google Business Profile.');
      setTimeout(() => {
        closeReplyModal();
        loadReviews();
      }, 1200);
    } catch (err: any) {
      setReplyError(err.message || 'Failed to publish review reply.');
    } finally {
      setReplySubmitting(false);
    }
  };

  // Handle AI Reply Suggestion
  const handleSuggestAiReply = async () => {
    if (!activeBizId || !replyingReview || generatingAi) return;

    setGeneratingAi(true);
    setAiError(null);

    try {
      const res = await reviewsApi.suggestAiReply(activeBizId, {
        reviewText: replyingReview.comment || 'General customer feedback',
        starRating: replyingReview.starRating,
        reviewerName: replyingReview.reviewer.displayName,
      });

      if (res?.suggestion) {
        setReplyComment(res.suggestion);
      } else {
        setAiError('Unable to generate an AI reply right now. Please try again later.');
      }
    } catch (err: any) {
      console.warn('AI reply suggestion failed:', err);
      setAiError('Unable to generate an AI reply right now. Please try again later.');
    } finally {
      setGeneratingAi(false);
    }
  };

  // Star visual renderer
  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          style={{
            color: i <= rating ? '#fbbf24' : '#4b5563',
            fontSize: '15px',
            marginRight: '1px',
          }}
        >
          
        </span>
      );
    }
    return <span style={{ display: 'inline-flex', alignItems: 'center' }}>{stars}</span>;
  };

  // Format date helper
  const formatDate = (isoString?: string) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return isoString;
    }
  };

  return (
    <DashboardLayout title="Reviews">
      <div className="page-container" style={{ paddingBottom: '3rem' }}>
        {/* HEADER */}
        <div className="page-header" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <span></span> Reviews
              </h1>
              <p className="page-description" style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted, #94a3b8)' }}>
                Monitor customer feedback, ratings, and review activity for your business.
              </p>
            </div>

            {data?.isConnected && data?.hasLocation && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSync}
                  disabled={syncing || loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <span className={syncing ? 'spinning' : ''} aria-hidden="true"></span>
                  {syncing ? 'Refreshing...' : 'Refresh Reviews'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ERROR BANNER */}
        {error && (
          <div
            className="alert alert-danger"
            style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              borderRadius: '8px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#f87171',
            }}
          >
            {error}
          </div>
        )}

        {/* GOOGLE DISCONNECTED EMPTY STATE */}
        {!loading && data && !data.isConnected && (
          <div
            className="card"
            style={{
              padding: '3rem 2rem',
              textAlign: 'center',
              background: 'var(--card-bg, #1e293b)',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #334155)',
              margin: '2rem auto',
              maxWidth: '640px',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
              Connect Google Business Profile
            </h2>
            <p style={{ color: 'var(--text-muted, #94a3b8)', maxWidth: '440px', margin: '0 auto 1.5rem auto' }}>
              Connect your Google Business Profile to view and manage customer reviews.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/businesses/${activeBizId}/integrations`)}
              style={{ padding: '0.75rem 1.5rem', fontWeight: 600 }}
            >
              Connect Google Business Profile
            </button>
          </div>
        )}

        {/* NO LOCATION CONNECTED STATE */}
        {!loading && data && data.isConnected && !data.hasLocation && (
          <div
            className="card"
            style={{
              padding: '3rem 2rem',
              textAlign: 'center',
              background: 'var(--card-bg, #1e293b)',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #334155)',
              margin: '2rem auto',
              maxWidth: '640px',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: '0 0 0.5rem 0' }}>
              No Location Selected
            </h2>
            <p style={{ color: 'var(--text-muted, #94a3b8)', maxWidth: '440px', margin: '0 auto 1.5rem auto' }}>
              Your Google account is connected, but no Google Business Profile location has been linked to this business yet.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/businesses/${activeBizId}/integrations`)}
              style={{ padding: '0.75rem 1.5rem', fontWeight: 600 }}
            >
              Select Business Location
            </button>
          </div>
        )}

        {/* GOOGLE API SETUP / CAPABILITY BLOCKER NOTICE */}
        {!loading && data && data.isConnected && data.hasLocation && data.apiError && (
          <div
            className="card"
            style={{
              padding: '1.25rem 1.5rem',
              marginBottom: '1.5rem',
              borderRadius: '10px',
              background: 'rgba(234, 179, 8, 0.08)',
              border: '1px solid rgba(234, 179, 8, 0.35)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}></span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 600, color: '#facc15' }}>
                Google Business Profile API Setup Required
              </h3>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                {data.apiError.message}
              </p>
              {data.apiError.helpUrl && (
                <a
                  href={data.apiError.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: '0.5rem',
                    fontSize: '0.85rem',
                    color: '#60a5fa',
                    textDecoration: 'underline',
                  }}
                >
                  Enable Google My Business API in Google Cloud Console 
                </a>
              )}
            </div>
          </div>
        )}

        {/* MAIN CONTENT AREA */}
        {!loading && data && data.isConnected && data.hasLocation && (
          <>
            {/* SUMMARY METRICS CARDS */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
              }}
            >
              {/* AVERAGE RATING */}
              <div className="card stat-card" style={{ padding: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #94a3b8)' }}>
                  Average Rating
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary, #f8fafc)' }}>
                    {data.summary.totalReviews > 0 ? data.summary.averageRating.toFixed(1) : ''}
                  </span>
                  {data.summary.totalReviews > 0 && renderStars(Math.round(data.summary.averageRating))}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #94a3b8)', marginTop: '0.25rem' }}>
                  Based on Google reviews
                </div>
              </div>

              {/* TOTAL REVIEWS */}
              <div className="card stat-card" style={{ padding: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #94a3b8)' }}>
                  Total Reviews
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary, #f8fafc)', marginTop: '0.5rem' }}>
                  {data.summary.totalReviews > 0 ? data.summary.totalReviews.toLocaleString() : '0'}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #94a3b8)', marginTop: '0.25rem' }}>
                  Verified customer reviews
                </div>
              </div>

              {/* UNANSWERED REVIEWS */}
              <div className="card stat-card" style={{ padding: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #94a3b8)' }}>
                  Unanswered Reviews
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 700, color: data.summary.unansweredReviews > 0 ? '#f59e0b' : '#10b981' }}>
                    {data.summary.unansweredReviews}
                  </span>
                  {data.summary.unansweredReviews > 0 && (
                    <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Needs Attention</span>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #94a3b8)', marginTop: '0.25rem' }}>
                  Awaiting owner reply
                </div>
              </div>

              {/* RESPONSE RATE */}
              <div className="card stat-card" style={{ padding: '1.25rem' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #94a3b8)' }}>
                  Response Rate
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#38bdf8', marginTop: '0.5rem' }}>
                  {data.summary.totalReviews > 0 ? `${data.summary.responseRate}%` : ''}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #94a3b8)', marginTop: '0.25rem' }}>
                  Owner reply percentage
                </div>
              </div>
            </div>

            {/* RATING DISTRIBUTION */}
            {data.summary.totalReviews > 0 && (
              <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem', borderRadius: '10px' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: 600 }}>Rating Breakdown</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { star: 5, count: data.summary.ratingDistribution.fiveStar },
                    { star: 4, count: data.summary.ratingDistribution.fourStar },
                    { star: 3, count: data.summary.ratingDistribution.threeStar },
                    { star: 2, count: data.summary.ratingDistribution.twoStar },
                    { star: 1, count: data.summary.ratingDistribution.oneStar },
                  ].map(({ star, count }) => {
                    const pct = data.summary.totalReviews > 0 ? (count / data.summary.totalReviews) * 100 : 0;
                    return (
                      <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem' }}>
                        <span style={{ width: '45px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          {star} <span style={{ color: '#fbbf24' }}></span>
                        </span>
                        <div style={{ flex: 1, height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background: star >= 4 ? '#10b981' : star === 3 ? '#f59e0b' : '#ef4444',
                              borderRadius: '4px',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <span style={{ width: '40px', textAlign: 'right', color: 'var(--text-muted, #94a3b8)', fontSize: '0.8rem' }}>
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* FILTER & SEARCH BAR */}
            <div
              className="card"
              style={{
                padding: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '10px',
              }}
            >
              {/* Search Input */}
              <div style={{ position: 'relative', minWidth: '240px', flex: '1 1 240px' }}>
                <input
                  type="text"
                  placeholder="Search reviews or reviewer..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="form-control"
                  style={{
                    width: '100%',
                    paddingLeft: '2rem',
                    fontSize: '0.875rem',
                    borderRadius: '6px',
                    background: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--border-color, #334155)',
                    color: 'inherit',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    left: '0.65rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted, #94a3b8)',
                    pointerEvents: 'none',
                  }}
                >
                  
                </span>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '0.5rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted, #94a3b8)',
                      cursor: 'pointer',
                    }}
                  >
                    
                  </button>
                )}
              </div>

              {/* Rating Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="rating-filter" style={{ fontSize: '0.85rem', color: 'var(--text-muted, #94a3b8)' }}>Rating:</label>
                <select
                  id="rating-filter"
                  value={ratingFilter}
                  onChange={(e) => {
                    setRatingFilter(e.target.value);
                    setPage(1);
                  }}
                  className="form-control"
                  style={{
                    fontSize: '0.85rem',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '6px',
                    background: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--border-color, #334155)',
                    color: 'inherit',
                  }}
                >
                  <option value="all">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>
              </div>

              {/* Reply Status Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="status-filter" style={{ fontSize: '0.85rem', color: 'var(--text-muted, #94a3b8)' }}>Status:</label>
                <select
                  id="status-filter"
                  value={replyStatusFilter}
                  onChange={(e) => {
                    setReplyStatusFilter(e.target.value as any);
                    setPage(1);
                  }}
                  className="form-control"
                  style={{
                    fontSize: '0.85rem',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '6px',
                    background: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--border-color, #334155)',
                    color: 'inherit',
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="unanswered">Unanswered</option>
                  <option value="replied">Replied</option>
                </select>
              </div>
            </div>

            {/* REVIEWS LIST */}
            {data.reviews.length === 0 ? (
              <div
                className="card"
                style={{
                  padding: '3rem 2rem',
                  textAlign: 'center',
                  borderRadius: '10px',
                  color: 'var(--text-muted, #94a3b8)',
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}></div>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'var(--text-primary, #f8fafc)' }}>
                  {searchTerm || ratingFilter !== 'all' || replyStatusFilter !== 'all'
                    ? 'No reviews match your filters'
                    : 'No reviews yet'}
                </h3>
                <p style={{ margin: 0, fontSize: '0.9rem', maxWidth: '420px', marginInline: 'auto' }}>
                  {searchTerm || ratingFilter !== 'all' || replyStatusFilter !== 'all'
                    ? 'Try clearing your filters or search query to see more reviews.'
                    : 'Once customers leave reviews on your Google Business Profile, they will appear here.'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {data.reviews.map((rev) => (
                  <div
                    key={rev.reviewId}
                    className="card"
                    style={{
                      padding: '1.25rem',
                      borderRadius: '10px',
                      border: '1px solid var(--border-color, #334155)',
                    }}
                  >
                    {/* Top Bar: Reviewer Info & Rating */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {rev.reviewer.profilePhotoUrl ? (
                          <img
                            src={rev.reviewer.profilePhotoUrl}
                            alt={rev.reviewer.displayName}
                            style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '50%',
                              background: '#334155',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              color: '#94a3b8',
                            }}
                          >
                            {rev.reviewer.displayName[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{rev.reviewer.displayName}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #94a3b8)' }}>
                            {formatDate(rev.createTime)}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {renderStars(rev.starRating)}
                        {rev.reviewReply ? (
                          <span className="badge" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#34d399', fontSize: '0.75rem' }}>
                             Replied
                          </span>
                        ) : rev.starRating <= 2 ? (
                          <span className="badge" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#f87171', fontSize: '0.75rem' }}>
                            Needs Response
                          </span>
                        ) : (
                          <span className="badge" style={{ backgroundColor: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', fontSize: '0.75rem' }}>
                            Unanswered
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Comment */}
                    <p style={{ margin: '0.75rem 0 1rem 0', fontSize: '0.925rem', lineHeight: '1.5', color: '#cbd5e1' }}>
                      {rev.comment || <em style={{ color: '#64748b' }}>No written feedback provided.</em>}
                    </p>

                    {/* Owner Reply Box (if replied) */}
                    {rev.reviewReply && (
                      <div
                        style={{
                          padding: '0.85rem 1rem',
                          marginBottom: '0.75rem',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(30, 41, 59, 0.7)',
                          borderLeft: '3px solid #38bdf8',
                          fontSize: '0.875rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <span style={{ fontWeight: 600, color: '#38bdf8', fontSize: '0.8rem' }}>Response from Owner</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{formatDate(rev.reviewReply.updateTime)}</span>
                        </div>
                        <p style={{ margin: 0, color: '#e2e8f0', lineHeight: '1.45' }}>
                          {rev.reviewReply.comment}
                        </p>
                      </div>
                    )}

                    {/* Reply Button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => openReplyModal(rev)}
                        style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                      >
                        {rev.reviewReply ? 'Edit Reply' : 'Reply to Review'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* PAGINATION */}
            {data.pagination.totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginTop: '1.5rem',
                  fontSize: '0.85rem',
                }}
              >
                <div style={{ color: 'var(--text-muted, #94a3b8)' }}>
                  Showing {(page - 1) * pageSize + 1}{Math.min(page * pageSize, data.pagination.totalItems)} of {data.pagination.totalItems} reviews
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '0 0.5rem' }}>
                    Page {page} of {data.pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={page >= data.pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* REPLY MODAL */}
        {replyingReview && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              zIndex: 1000,
            }}
          >
            <div
              className="card"
              style={{
                width: '100%',
                maxWidth: '560px',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color, #334155)',
                backgroundColor: 'var(--card-bg, #1e293b)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600 }}>
                  {replyingReview.reviewReply ? 'Edit Review Reply' : 'Reply to Customer Review'}
                </h3>
                <button
                  type="button"
                  onClick={closeReplyModal}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer' }}
                >
                  
                </button>
              </div>

              {/* Quoted Review */}
              <div
                style={{
                  padding: '0.85rem',
                  backgroundColor: 'rgba(15, 23, 42, 0.6)',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <strong>{replyingReview.reviewer.displayName}</strong>
                  {renderStars(replyingReview.starRating)}
                </div>
                <p style={{ margin: 0, color: '#94a3b8' }}>
                  "{replyingReview.comment || 'No text review'}"
                </p>
              </div>

              {/* Suggest Reply with AI action */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label htmlFor="reply-textarea" style={{ fontSize: '0.85rem', fontWeight: 600 }}>Your Reply:</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSuggestAiReply}
                  disabled={generatingAi}
                  style={{
                    fontSize: '0.8rem',
                    padding: '0.3rem 0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                  }}
                >
                  <span></span>
                  {generatingAi ? 'Generating AI Suggestion...' : 'Suggest Reply with AI'}
                </button>
              </div>

              {/* AI Error notice (failsafe fallback) */}
              {aiError && (
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: '#f87171',
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  }}
                >
                  {aiError}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmitReply}>
                <textarea
                  id="reply-textarea"
                  rows={4}
                  value={replyComment}
                  onChange={(e) => setReplyComment(e.target.value)}
                  placeholder="Write a courteous, professional reply to this customer..."
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    borderRadius: '6px',
                    background: 'var(--input-bg, #0f172a)',
                    border: '1px solid var(--border-color, #334155)',
                    color: 'inherit',
                    resize: 'vertical',
                    marginBottom: '0.75rem',
                  }}
                  required
                />

                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '1rem' }}>
                   Replies will be published on Google Business Profile under your verified business name.
                </div>

                {replyError && (
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: '#f87171',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    }}
                  >
                    {replyError}
                  </div>
                )}

                {replySuccess && (
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: '#34d399',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    }}
                  >
                    {replySuccess}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeReplyModal}
                    disabled={replySubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={replySubmitting || !replyComment.trim()}
                  >
                    {replySubmitting ? 'Publishing...' : 'Publish Reply'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
